import { checkoutService } from 'core/services/checkout/checkout.service';
import type { TSliceExtraReducer } from 'core/store';
import { authGetCustomerSelector } from 'core/store/auth/selectors';
import { createAppAsyncThunk } from 'core/store/store.utils';
import { handleServiceException } from 'shared/utils/errors.utils';
import {
  checkHasNoCreditsForNonHybrid,
  getCustomerIdFromCheckout,
  getInitialCheckoutParams,
  getInitialCheckoutValues,
} from '../checkout-state.utils';
import { checkoutInitialState, resetCheckoutLoading, SLICE_NAMESPACE } from '../constants';
import { ICheckoutState, IGetInitialCheckoutDetail } from '../types';

export const getCheckoutDetailAction = createAppAsyncThunk<IGetInitialCheckoutDetail, void>(
  `${SLICE_NAMESPACE}/getCheckoutDetail`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const { checkout } = state;
      const customer = authGetCustomerSelector(state);
      const { source } = customer;

      const customerId = getCustomerIdFromCheckout(checkout);

      const params = getInitialCheckoutParams({
        checkoutDetail: checkout.detail,
        checkoutPaymentMethod: checkout.paymentMethod,
        checkoutAddress: checkout.address,
        customerSource: source,
      });

      const detail = await checkoutService.getCheckoutDetail(customerId, source, params);
      const initialValues = getInitialCheckoutValues(detail, customer);
      const hasNoCreditsForNonHybrid = checkHasNoCreditsForNonHybrid(detail, source);

      return {
        detail,
        initialValues,
        hasNoCreditsForNonHybrid,
      };
    } catch (error) {
      return rejectWithValue(handleServiceException(error));
    }
  },
);

export const getCheckoutDetailReducer: TSliceExtraReducer<ICheckoutState> = builder => {
  builder
    .addCase(getCheckoutDetailAction.pending, state => {
      state.loading = {
        global: true,
        alicorpCart: false,
        alliedCart: false,
        generalCart: false,
      };
      state.error = checkoutInitialState.error;
    })
    .addCase(getCheckoutDetailAction.fulfilled, (state, { payload }) => {
      const { detail, initialValues, hasNoCreditsForNonHybrid } = payload;
      state.loading = resetCheckoutLoading;
      state.error = checkoutInitialState.error;
      state.detail = detail;
      state.hasNoCreditsForNonHybrid = hasNoCreditsForNonHybrid;

      if (state.shipping.idle) {
        state.shipping.deliveryDates = initialValues.deliveryDates;
        state.paymentMethod = initialValues.paymentMethod;
        state.shipping.idle = false;
      }
    })
    .addCase(getCheckoutDetailAction.rejected, (state, { payload }) => {
      const { shipping, paymentMethod, error: initialError } = checkoutInitialState;
      state.loading = resetCheckoutLoading;
      if (payload)
        state.error = {
          ...initialError,
          global: payload,
        };
      state.paymentMethod = paymentMethod;
      state.shipping = shipping;
    });

  return builder;
};
